import { useState } from "react";
import { ApiError } from "../../api/client";
import type { Authorization, Order } from "../../api/types";
import { useTill } from "../../app/till-context";
import { t } from "../../i18n";
import { formatEuro } from "../../lib/money";
import { Dialog } from "../../ui/Dialog";
import { cancelToasts } from "../printing/print-notices";
import { mailsCustomer, paymentOf } from "./order-view";

interface Props {
  order: Order;
  /** The code of the manager or owner who cancels; their name goes into the history. */
  authorization: Authorization;
  onDone: (order: Order) => void;
  onClose: () => void;
}

/** Cancelling, as in the prototype: a reason from the list, and what happens next spelled out. */
export function CancelDialog({ order, authorization, onDone, onClose }: Props) {
  const { api, toast, printStatus } = useTill();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cancel = async () => {
    setBusy(true);
    setError(null);
    try {
      const updated = await api.request<Order>("POST", `/orders/${order.id}/cancel`, {
        staffToken: authorization.token,
        body: { reason },
      });
      onDone(updated);
      toast(t.orders.cancelledBy(order.code, authorization.staff.name));
      if (updated.mails > order.mails) toast(t.orders.mails.sentTo(order.customer.email ?? ""));
      // the cash back out of the drawer, GEANNULEERD to the kitchen (blok I)
      for (const text of cancelToasts(updated.prints)) toast(text);
      onClose();
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : t.errors.unexpected);
      setBusy(false);
    }
  };

  return (
    <Dialog
      title={t.orders.cancelTitle}
      size="narrow"
      onClose={onClose}
      footer={
        <>
          <span className="spacer" />
          <button type="button" className="btn" onClick={onClose} data-autofocus>
            {t.orders.back}
          </button>
          <button type="button" className="btn danger" disabled={busy || reason === ""} onClick={() => void cancel()}>
            {t.orders.confirmCancel}
          </button>
        </>
      }
    >
      <p>
        {t.orders.code} <span className="code">{order.code}</span> · {formatEuro(order.totalCents)} · {paymentOf(order)}
      </p>
      <label className="field">
        {t.orders.cancelReason}
        <select
          value={reason}
          onChange={(event) => {
            setReason(event.target.value);
          }}
        >
          <option value="">{t.orders.chooseReason}</option>
          {t.orders.cancelReasons.map((option) => (
            <option key={option}>{option}</option>
          ))}
        </select>
      </label>
      <div className="warnbox">
        <b>{t.orders.whatHappens}</b>
        <br />
        {order.paidCents > 0 ? t.orders.giveBackCash(formatEuro(order.paidCents)) : t.orders.nothingPaid}
        <br />
        {mailsCustomer(order) ? t.orders.customerMailed : t.orders.customerUnknown}
        <br />
        {/* prototype: an open order's kitchen gets GEANNULEERD (a shop with printers) */}
        {order.status === "open" && (printStatus?.printers ?? 0) > 0
          ? t.orders.ownerAndKitchen
          : t.orders.ownerAndReports}
        <br />
        {t.orders.finalAfterCancel}
      </div>
      {error !== null && (
        <p className="error-text" role="alert">
          {error}
        </p>
      )}
    </Dialog>
  );
}
